import React, { useCallback, useEffect, useState } from "react";
import { FormControlLabel, Radio, RadioGroup } from "@mui/material";
import SelectComponent from "components/selectAutoCompleteG";
import { DatePicker } from "screens/perfilAutonomo/components/DatePicker";
import { SecondaryButton } from "screens/perfilAutonomo/components/secondaryButton.component";
import { Loading } from "screens/perfilAutonomo/components/Loading";
import { ExcelIcon } from "assets/iconsSVG/Excel";
import { debounce, FileUploadI } from "screens/perfilAutonomo/utils";
import { toast } from "react-toastify";
import * as S from "./style";
import { defaultRVData } from "./utils";
import { fetchRevendaEmpreendimentosListar } from "../unidades/api/postRevendaEmpreendimentoListar";
import { fetchRevendaAreaUtil } from "../unidades/api/getRevendaAreaUtil";
import { postListarResponsaveisRevenda } from "./api/postListarResponsaveisRevenda";
import { postListarEquipesRevenda } from "./api/postListarEquipesRevenda";
import { NumberOptions } from "../unidades/novaRevenda/utils";
import { postRelatorioRV } from "./api/postRelatorioRV";

const today = new Date();

export const ReservadoVendidoComponent = ({
  authToken,
  shouldClear,
}: {
  authToken: string;
  shouldClear: boolean;
}) => {
  const [data, setData] = useState(defaultRVData);
  const [submitting, setSubmitting] = useState(false);

  const [responsaveisOptions, setResponsaveisOptions] = useState<any[]>([]);
  const [loadingResponsaveis, setLoadingResponsaveis] = useState(true);
  const [equipeOptions, setEquipeOptions] = useState<any[]>([]);
  const [loadingEquipe, setLoadingEquipe] = useState(true);

  const {
    data: revendaEmpreendimentoOptions,
    isLoading: loadingEmpreendimentos,
  } = fetchRevendaEmpreendimentosListar({
    enabled: true,
    authToken,
    registros: 5000,
  });
  const torreOptions =
    revendaEmpreendimentoOptions &&
    data?.idRevendaEmpreendimento &&
    revendaEmpreendimentoOptions?.find(
      (item: any) => item?.id === data?.idRevendaEmpreendimento
    )?.revendaEmpreendimentoTorres?.length > 0
      ? revendaEmpreendimentoOptions
          ?.find((item: any) => item?.id === data?.idRevendaEmpreendimento)
          ?.revendaEmpreendimentoTorres?.map((item: any) => ({
            label: item?.descricao,
            value: item?.id,
          }))
      : [];
  const { data: areaOptions, isLoading: loadingArea } = fetchRevendaAreaUtil({
    enabled: true,
    authToken,
  });
  const fetchEquipes = useCallback(
    debounce((data_) => {
      if (!data_.idRevendaEmpreendimento) return;

      setLoadingEquipe(true);

      postListarEquipesRevenda({
        authToken,
        tipoRelatorio: data_.tipoRelatorio,
        idEmpreendimento: data_.idRevendaEmpreendimento,
      })
        .then(({ success, data: res, notifications }) => {
          if (!success) throw new Error(notifications?.[0]?.message ?? "");
          setEquipeOptions(res?.data);
        })
        .catch((err) => {
          console.error("Error fetching equipes", err);
          toast.error(
            err?.message ? `Erro: ${err?.message}` : "Erro ao buscar equipes"
          );
        })
        .finally(() => {
          setLoadingEquipe(false);
        });
    }, 1000),
    []
  );
  const fetchResposaveis = useCallback(
    debounce((data_) => {
      if (!data_.idRevendaEmpreendimento || !data_.idEquipe) return;

      setLoadingResponsaveis(true);

      postListarResponsaveisRevenda({
        authToken,
        tipoRelatorio: data_.tipoRelatorio,
        idEmpreendimento: data_.idRevendaEmpreendimento,
        idEquipe: data_.idEquipe,
      })
        .then(({ success, data: res, notifications }) => {
          if (!success) throw new Error(notifications?.[0]?.message ?? "");
          setResponsaveisOptions(res?.data);
        })
        .catch((err) => {
          console.error("Error fetching responsaveis", err);
          toast.error(
            err?.message
              ? `Erro: ${err?.message}`
              : "Erro ao buscar responsáveis"
          );
        })
        .finally(() => {
          setLoadingResponsaveis(false);
        });
    }, 1000),
    []
  );

  useEffect(() => {
    handleClear();
  }, [shouldClear]);

  const handleClear = () =>
    setData((prev) => ({
      ...defaultRVData,
      tipoRelatorio: prev?.tipoRelatorio,
    }));

  const handleDownload = ({ nome, base64, contentType }: FileUploadI) => {
    try {
      const fullBase64 = `data:${contentType};base64,${base64}`;
      const link = document.createElement("a");
      link.href = fullBase64;
      link.download = nome ?? "relatorio.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Relatório gerado com sucesso");
    } catch (err) {
      console.error("Download failed: ", err);
      toast.error("Erro ao baixar relatório");
    }
  };

  const handleSubmit = () => {
    setSubmitting(true);

    postRelatorioRV({ authToken, ...data })
      .then(({ success, data: res, notifications }) => {
        if (!success) throw new Error(notifications?.[0]?.message ?? "");
        if (success && !res) {
          toast.info("Nenhum dado encontrado para os parâmetros informados");
          return;
        }
        handleDownload(res);
      })
      .catch((err) => {
        console.error("Error fetching equipes", err);
        toast.error(
          err?.message ? `Erro: ${err?.message}` : "Erro ao buscar equipes"
        );
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <S.Container>
      <S.Flex>
        <RadioGroup
          aria-labelledby="radio-buttons-group-label"
          value={data?.tipoRelatorio}
          name="radio-buttons-group"
          row
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setData((prev) => ({
              ...prev,
              tipoRelatorio: e?.target?.value,
            }));
          }}
        >
          <FormControlLabel value={"R"} control={<Radio />} label="Reservado" />
          <FormControlLabel value={"V"} control={<Radio />} label="Vendido" />
        </RadioGroup>
      </S.Flex>

      <S.Row template="1fr 1fr 1fr">
        <S.Field>
          <p className="label">{"Empreendimento"}</p>
          <SelectComponent
            data={
              revendaEmpreendimentoOptions
                ? revendaEmpreendimentoOptions?.map((item: any) => ({
                    label: item?.nome,
                    value: item?.id,
                  }))
                : []
            }
            onChange={(e: { label: string; value: string }) => {
              setData((prev) => {
                const newData = {
                  ...prev,
                  idRevendaEmpreendimento: e?.value ?? "",
                  idRevendaEmpreendimentoTorre: "",
                  idEquipe: "",
                  idResponsavel: "",
                };
                if (e?.value) setLoadingEquipe(true);
                fetchEquipes(newData);
                return newData;
              });
            }}
            value={
              revendaEmpreendimentoOptions
                ?.map((item: any) => ({
                  label: item?.nome,
                  value: item?.id,
                }))
                ?.find(
                  (item: any) => item?.value === data?.idRevendaEmpreendimento
                ) ?? ""
            }
            disabled={
              loadingEmpreendimentos ||
              !revendaEmpreendimentoOptions ||
              revendaEmpreendimentoOptions?.length === 0
            }
            borderRadius="8px"
          />
        </S.Field>
        <S.Field>
          <p className="label">{"Torre"}</p>
          <SelectComponent
            data={torreOptions}
            onChange={(e: { label: string; value: string }) => {
              setData((prev) => ({
                ...prev,
                idRevendaEmpreendimentoTorre: e?.value ?? "",
              }));
            }}
            value={
              torreOptions?.find(
                (item: any) =>
                  item?.value === data?.idRevendaEmpreendimentoTorre
              ) ?? ""
            }
            disabled={loadingEmpreendimentos || torreOptions?.length === 0}
            borderRadius="8px"
          />
        </S.Field>
        <S.Field>
          <p className="label">{"Equipe"}</p>
          <SelectComponent
            data={
              equipeOptions
                ? equipeOptions?.map((item: any) => ({
                    label: item?.nome,
                    value: item?.id,
                  }))
                : []
            }
            onChange={(e: { label: string; value: string }) => {
              setData((prev) => {
                const newData = {
                  ...prev,
                  idEquipe: e?.value ?? "",
                  idResponsavel: "",
                };
                fetchResposaveis(newData);
                return newData;
              });
            }}
            value={
              equipeOptions
                ?.map((item: any) => ({
                  label: item?.nome,
                  value: item?.id,
                }))
                ?.find((item: any) => item?.value === data?.idEquipe) ?? ""
            }
            disabled={
              loadingEquipe ||
              equipeOptions?.length === 0 ||
              !data?.idRevendaEmpreendimento
            }
            borderRadius="8px"
          />
        </S.Field>
      </S.Row>

      <S.Row template="1fr 1fr 1fr">
        <S.Field>
          <p className="label">{"Vagas"}</p>
          <SelectComponent
            data={NumberOptions}
            onChange={(e: { label: string; value: number }) => {
              setData((prev) => ({
                ...prev,
                vagas: typeof e?.value === "number" ? e?.value : null,
              }));
            }}
            value={
              NumberOptions?.find((item: any) => item?.value === data?.vagas) ??
              ""
            }
            borderRadius="8px"
          />
        </S.Field>
        <S.Field>
          <p className="label">{"Área útil"}</p>
          <SelectComponent
            data={areaOptions ?? []}
            onChange={(e: { label: string; value: number }) => {
              setData((prev) => ({
                ...prev,
                areaUtil: typeof e?.value === "number" ? e?.value : null,
              }));
            }}
            value={
              areaOptions?.find(
                (item: any) => item?.value === data?.areaUtil
              ) ?? ""
            }
            disabled={loadingArea || areaOptions?.length === 0}
            borderRadius="8px"
          />
        </S.Field>
        <S.Field>
          <p className="label">{"Responsável"}</p>
          <SelectComponent
            data={
              responsaveisOptions
                ? responsaveisOptions?.map((item: any) => ({
                    label: item?.nome,
                    value: item?.id,
                  }))
                : []
            }
            onChange={(e: { label: string; value: string }) => {
              setData((prev) => ({
                ...prev,
                idResponsavel: e?.value ?? "",
              }));
            }}
            value={
              responsaveisOptions
                ?.map((item: any) => ({ label: item?.nome, value: item?.id }))
                ?.find((item: any) => item?.value === data?.idResponsavel) ?? ""
            }
            disabled={
              loadingResponsaveis ||
              responsaveisOptions?.length === 0 ||
              !data?.idEquipe ||
              !data?.idRevendaEmpreendimento
            }
            borderRadius="8px"
          />
        </S.Field>
      </S.Row>

      <S.Row template="1fr 1fr 1fr">
        <S.Row>
          <S.Field>
            <p className="label">{"Período de"}</p>
            <DatePicker
              value={data.periodoDe}
              onChange={(val: any) => {
                setData((prev) => ({
                  ...prev,
                  periodoDe: val,
                }));
              }}
              maxDate={data.periodoAte || today}
            />
          </S.Field>
          <S.Field>
            <p className="label">{"Período até"}</p>
            <DatePicker
              value={data.periodoAte}
              onChange={(val: any) => {
                setData((prev) => ({
                  ...prev,
                  periodoAte: val,
                }));
              }}
              minDate={data.periodoDe || undefined}
              maxDate={today}
            />
          </S.Field>
        </S.Row>
        <div id="dummy" />
        <div id="dummy" />
      </S.Row>

      <S.Row>
        <div id="dummy" />
        <S.Row>
          <SecondaryButton content={"Limpar filtros"} onClick={handleClear} />
          <SecondaryButton
            content={
              submitting ? (
                <div style={{ minWidth: 100 }}>
                  <Loading size={18} color="green" />
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                  }}
                >
                  <ExcelIcon />
                  {"Gerar Excel"}
                </div>
              )
            }
            color="success"
            style={{ padding: "0 10px" }}
            onClick={handleSubmit}
          />
        </S.Row>
      </S.Row>
    </S.Container>
  );
};
